import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskLine } from "../lib/reveal";
import { ArrowDownRight } from "lucide-react";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[92vh] pt-32 md:pt-40 pb-16 px-5 md:px-10 max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-end"
    >
      <div className="lg:col-span-7">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-6 flex items-center gap-3"
        >
          <span className="inline-block w-10 h-px bg-primary" />
          Built-environment consultancy growth
        </motion.p>

        <h1 className="font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl lg:text-[5.6rem]">
          <MaskLine delay={0.15}>We help you</MaskLine>
          <MaskLine delay={0.28}>
            <span className="text-primary">build</span> a better
          </MaskLine>
          <MaskLine delay={0.41}>practice.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-8 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed"
        >
          PLACE Ventures partners with architects, planners and the wider built
          environment to grow revenue and sharpen the processes behind the work —
          without the corporate consultancy theatre.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#booking"
            data-testid="hero-book-cta"
            className="group bg-foreground text-background font-semibold px-7 py-4 flex items-center gap-2 hover:-translate-y-0.5 transition-transform duration-200 hover:hard-shadow"
          >
            Book a discovery call
            <ArrowDownRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
          </a>
          <a
            href="#services"
            data-testid="hero-services-link"
            className="font-semibold px-2 py-4 border-b-2 border-transparent hover:border-primary transition-colors duration-200"
          >
            Explore what we do
          </a>
        </motion.div>
      </div>

      <div className="lg:col-span-5">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden aspect-[3/4] w-full"
        >
          <motion.img
            src="https://images.pexels.com/photos/2747599/pexels-photo-2747599.jpeg"
            alt="Sharp geometric concrete architecture"
            style={{ y: imgY, scale: imgScale }}
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/60 to-transparent">
            <span className="font-mono text-[11px] tracking-widest uppercase text-white/90">
              Fig. 01 — Structure & clarity
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
